// web/src/shell/NotFoundPage.tsx
//
// Used by both the marketing and the app routers as the catch-all
// element. The surrounding layout (MarketingLayout / AppShell) provides
// the chrome; this component only owns the body.
//
// The "home" link target depends on context: the marketing build sends
// users back to "/" (apex landing); the app build sends them to
// /dashboard (auth-gated). The caller passes the destination + label.
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { Compass } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";

interface Props {
  /** Destination for the primary CTA (e.g. "/" or "/dashboard"). */
  homeTo: string;
  /** Label for the primary CTA (e.g. "Back to landing" / "Back to dashboard"). */
  homeLabel: string;
}

export function NotFoundPage({ homeTo, homeLabel }: Props) {
  const location = useLocation();
  const { t } = useTranslation("common");
  return (
    <main className="container mx-auto flex min-h-[60vh] max-w-2xl flex-col items-center justify-center px-4 py-16 text-center">
      <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-secondary text-secondary-foreground">
        <Compass className="h-7 w-7" aria-hidden="true" />
      </div>
      <h1 className="mb-2 text-3xl font-semibold tracking-tight">
        {t("notFound.title")}
      </h1>
      <p className="mb-1 text-sm text-muted-foreground">
        <Trans
          i18nKey="common:notFound.missingPath"
          values={{ pathname: location.pathname }}
          components={{
            path: <code className="rounded bg-muted px-1.5 py-0.5 text-xs" />,
          }}
        />
      </p>
      <p className="mb-6 text-sm text-muted-foreground">
        {t("notFound.hint")}
      </p>
      <Link
        to={homeTo}
        className="rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90"
      >
        {homeLabel}
      </Link>
    </main>
  );
}
